import { api } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import type { PlatformStats } from "../types";
import { Sparkline } from "./Sparkline";
import { lastDeltaPct } from "../sparkline";
import { fmtDuration, fmt } from "../format";

/**
 * The platform at a glance — operator-only, spans every tenant.
 *
 * Holds the kintsugi rule: success has no colour. Clean runs are plain ink; gold appears only
 * where something was repaired or a fix is waiting on a human; iron-oxide only where something
 * is failing right now (failed runs, paused scrapers, dead letters, spend near the breaker).
 */
export function Overview({
  onNav,
  onOpenTenant,
  onOpenRuns,
  onOpenScrapers,
}: {
  onNav: (tab: "review" | "clients" | "dlq") => void;
  onOpenTenant: (tenantId: string) => void;
  onOpenRuns: (status?: "failed") => void;
  onOpenScrapers: (status?: "paused") => void;
}) {
  const { data, loading, error, reload } = useAsync<PlatformStats>(() => api("/stats"), [], 30_000);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  const { tenants, scrapers, runs24h, heals24h, canariesWaiting, dlqTotal, budget, topFailing,
    topFailingClients, recentEscalations, trends, healMttrSec, inFlight, queueDepth, queues,
    schedulerLastTickSec } = data;
  // Stale if the scheduler hasn't ticked in >30s (default poll is 5s → ~6 missed cycles).
  const schedulerStale = schedulerLastTickSec !== null && schedulerLastTickSec > 30;
  const clean = runs24h.total > 0 ? Math.round((runs24h.success / runs24h.total) * 100) : null;
  // Heal spend against the daily kill-switch budget — the fuse an operator watches.
  const cap = budget?.dailyUsdCap ?? 0;
  const spendPct = cap > 0 ? Math.round((heals24h.spendUsd / cap) * 100) : null;
  const nearCap = spendPct !== null && spendPct >= 80;

  // 7-day series for the sparklines (oldest→newest).
  const runsSeries = trends.map((t) => t.runs);
  const successSeries = trends.map((t) => (t.runs > 0 ? Math.round((t.success / t.runs) * 100) : 0));
  const spendSeries = trends.map((t) => t.healSpend);
  const runsDelta = lastDeltaPct(runsSeries);
  const successDelta = lastDeltaPct(successSeries);
  const totalRuns7d = runsSeries.reduce((a, b) => a + b, 0);
  const spend7d = spendSeries.reduce((a, b) => a + b, 0);
  const deltaTag = (d: number | null) =>
    d === null ? null : (
      <span className="muted sm">
        {d > 0 ? "+" : ""}
        {d}% vs prev
      </span>
    );

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Overview</h1>
        <RefreshButton onClick={reload} />
      </div>

      <p className="fleet-line lead">
        <em>{tenants.total}</em> client{tenants.total === 1 ? "" : "s"} · <em>{scrapers.total}</em>{" "}
        scraper{scrapers.total === 1 ? "" : "s"} ·{" "}
        {canariesWaiting === 0 ? (
          "nothing waiting"
        ) : (
          <em className="gold">
            {canariesWaiting} fix{canariesWaiting === 1 ? "" : "es"} waiting on you
          </em>
        )}
      </p>

      <div className="health-bar">
        <span className={inFlight > 0 ? "hb-item gold" : "hb-item"}>
          <b>{inFlight}</b> in flight
        </span>
        <span className="hb-item">
          <b>{queueDepth}</b> queued
          {queueDepth > 0 && (
            <span className="muted sm">
              {" "}
              ({queues.jobs} jobs · {queues.broken} heals · {queues.results} deliveries)
            </span>
          )}
        </span>
        <span className={schedulerStale ? "hb-item bad" : "hb-item"} title="Time since the scheduler's last poll">
          scheduler{" "}
          <b>{schedulerLastTickSec === null ? "—" : `${fmtDuration(schedulerLastTickSec)} ago`}</b>
        </span>
      </div>

      <section>
        <h3>Trends · last 7 days</h3>
        <div className="trend-tiles">
          <div className="trend-tile">
            <div className="tt-head">
              <span className="muted sm">Runs/day</span>
              {deltaTag(runsDelta)}
            </div>
            <div className="tt-val">{totalRuns7d}</div>
            <Sparkline className="tt-spark" values={runsSeries} />
          </div>
          <div className="trend-tile">
            <div className="tt-head">
              <span className="muted sm">Success %</span>
              {deltaTag(successDelta)}
            </div>
            <div className="tt-val">{successSeries[successSeries.length - 1] ?? 0}%</div>
            <Sparkline className="tt-spark" values={successSeries} />
          </div>
          <div className="trend-tile">
            <div className="tt-head">
              <span className="muted sm">Heal spend</span>
            </div>
            <div className="tt-val">${spend7d.toFixed(2)}</div>
            <Sparkline className="tt-spark" values={spendSeries} />
          </div>
        </div>
      </section>

      {topFailingClients.length > 0 && (
        <section>
          <h3>Needs attention — clients (24h)</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th className="num">Failed runs</th>
                  <th className="num">Scrapers</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {topFailingClients.map((c) => (
                  <tr key={c.tenantId}>
                    <td>{c.name}</td>
                    <td className="num">
                      <span className="err">{c.failures}</span>
                    </td>
                    <td className="num">{c.scrapers}</td>
                    <td className="acts">
                      <button className="btn ghost sm" onClick={() => onOpenTenant(c.tenantId)}>
                        Open client →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {topFailing.length > 0 && (
        <section>
          <h3>Needs attention — most failures (24h)</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Scraper</th>
                  <th className="num">Failures</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {topFailing.map((f) => (
                  <tr key={f.id}>
                    <td>{f.name}</td>
                    <td className="num">
                      <span className="err">{f.failures}</span>
                    </td>
                    <td className="acts">
                      <button className="btn ghost sm" onClick={() => onOpenTenant(f.tenantId)}>
                        Open client →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {recentEscalations.length > 0 && (
        <section>
          <h3>Human needed — healing gave up (7d)</h3>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Scraper</th>
                  <th>Escalated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {recentEscalations.map((e) => (
                  <tr key={e.id}>
                    <td>{e.name}</td>
                    <td className="nowrap muted">{fmt(e.at)}</td>
                    <td className="acts">
                      <button className="btn ghost sm" onClick={() => onOpenTenant(e.tenantId)}>
                        Open client →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h3>Runs · last 24 hours</h3>
        <div className="health-bar">
          {/* The whole-fleet run log — click through to browse/filter it. */}
          <button className="hb-item hb-btn" onClick={() => onOpenRuns()} title="Open the fleet run log">
            <b>{runs24h.total}</b> run{runs24h.total === 1 ? "" : "s"} →
          </button>
          {clean !== null && (
            <span className="hb-item">
              <b>{clean}%</b> clean
            </span>
          )}
          {runs24h.failed > 0 && (
            <button
              className="hb-item bad hb-btn"
              onClick={() => onOpenRuns("failed")}
              title="Open the failed runs"
            >
              <b>{runs24h.failed}</b> failed →
            </button>
          )}
        </div>
      </section>

      <section>
        <h3>Healing · last 24 hours</h3>
        <div className="health-bar">
          {heals24h.healed > 0 && (
            <span className="hb-item gold">
              <b>{heals24h.healed}</b> repaired
            </span>
          )}
          {heals24h.escalated > 0 && (
            <span className="hb-item gold">
              <b>{heals24h.escalated}</b> escalated
            </span>
          )}
          {heals24h.failed > 0 && (
            <span className="hb-item bad">
              <b>{heals24h.failed}</b> unrepaired
            </span>
          )}
          {heals24h.attempts > 0 && (
            <span className="hb-item">
              <b>{heals24h.attempts}</b> attempt{heals24h.attempts === 1 ? "" : "s"}
            </span>
          )}
          {healMttrSec !== null && (
            <span className="hb-item" title="Mean time from first heal attempt to a validated fix (7d)">
              <b>{fmtDuration(healMttrSec)}</b> avg repair
            </span>
          )}
          {/* Spend against the daily budget — the fuse. Iron-oxide once it's within 20% of the cap. */}
          <span className={nearCap ? "hb-item bad" : "hb-item"}>
            <b>${heals24h.spendUsd.toFixed(2)}</b>
            {cap > 0 ? ` of $${cap.toFixed(0)}` : ""} spent
            {spendPct !== null ? ` (${spendPct}%)` : ""}
          </span>
        </div>
        {heals24h.attempts === 0 && <p className="muted sm">No healing in the last 24 hours.</p>}
      </section>

      <section>
        <h3>Platform</h3>
        <div className="health-bar">
          <span className="hb-item">
            <b>{tenants.active}</b> active client{tenants.active === 1 ? "" : "s"}
            {tenants.total !== tenants.active && <span className="muted"> / {tenants.total}</span>}
          </span>
          <span className="hb-item">
            <b>{scrapers.active}</b> collecting
          </span>
          {scrapers.paused > 0 && (
            <button
              className="hb-item bad hb-btn"
              onClick={() => onOpenScrapers("paused")}
              title="Open the paused scrapers"
            >
              <b>{scrapers.paused}</b> paused →
            </button>
          )}
          {/* Dead letters — a healthy platform has zero; any is worth a click. */}
          {dlqTotal > 0 ? (
            <button className="hb-item bad hb-btn" onClick={() => onNav("dlq")}>
              <b>{dlqTotal}</b> dead letter{dlqTotal === 1 ? "" : "s"} →
            </button>
          ) : (
            <span className="hb-item">
              <b>0</b> dead letters
            </span>
          )}
        </div>
      </section>

      {(canariesWaiting > 0 || scrapers.paused > 0 || dlqTotal > 0) && (
        <div className="row-gap">
          {(canariesWaiting > 0 || scrapers.paused > 0) && (
            <button className="btn sm" onClick={() => onNav("review")}>
              Open Waiting →
            </button>
          )}
          {dlqTotal > 0 && (
            <button className="btn sm" onClick={() => onNav("dlq")}>
              Open Dead letters →
            </button>
          )}
          <button className="btn ghost sm" onClick={() => onNav("clients")}>
            Browse clients
          </button>
        </div>
      )}
    </div>
  );
}
